import { useState } from "react"
import { useNavigate } from "react-router-dom"

import { Datasets } from "../../../../constants/enums"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import {
    DEMAND_DEVICE_SEARCH_BASE,
    GEN_DEVICE_SEARCH,
} from "../../../../router/routes"
import { snackbar } from "../../../../utils/general/generalUtils"
import { deleteClientProtected } from "../../clients/clients"
import { DELETE_GEN_DEVICE, DELETE_LCT_DEVICE } from "../../urls/private"

interface IDeleteGenDevice {
    isDeleting: boolean
    deleteGenDevice: (deviceSystemRef: string) => Promise<void>
    deleteDemandDevice: (
        deviceSystemRef: string,
        dataset: Datasets
    ) => Promise<void>
}

/**
 * Provides functions for deleting device from the database - works for
 * both generation and demand devices.
 * @returns {IDeleteAlertHook} - loading and error states.
 */
const useDeleteDevice = (): IDeleteGenDevice => {
    const navigate = useNavigate()
    const [isDeleting, setIsDeleting] = useState(false)
    const deleteGenDevice = async (deviceSystemRef: string): Promise<void> => {
        try {
            // Build response url
            setIsDeleting(true)
            await deleteClientProtected(DELETE_GEN_DEVICE(deviceSystemRef))
            snackbar(
                `${deviceSystemRef} has been successfully deleted`,
                "success"
            )
            navigate(GEN_DEVICE_SEARCH)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            snackbar(errorObj.displayDetail, "error")
        } finally {
            setIsDeleting(false)
        }
    }

    const deleteDemandDevice = async (
        deviceSystemRef: string,
        dataset: Datasets
    ): Promise<void> => {
        try {
            // Build response url
            setIsDeleting(true)
            await deleteClientProtected(
                DELETE_LCT_DEVICE(deviceSystemRef, dataset)
            )
            snackbar(
                `${deviceSystemRef} has been successfully deleted`,
                "success"
            )
            navigate(`${DEMAND_DEVICE_SEARCH_BASE}/${dataset}`)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            snackbar(errorObj.displayDetail, "error")
        } finally {
            setIsDeleting(false)
        }
    }

    return {
        isDeleting,
        deleteGenDevice,
        deleteDemandDevice,
    }
}

export default useDeleteDevice
